import moment from "moment";
import moment_timzone from "moment-timezone";
/**
 * Checks if a given date string is valid and greater than the minimum date string.
 *
 * @param dateString - The date string to validate and compare.
 * @param minDateString - The minimum date string to compare against.
 * @returns `true` if the date is valid and greater than the minimum date, otherwise `false`.
 */
const SdsDtIsValidAndGreaterThanMinDate = (
  dateString: string,
  minDateString: string
) => {
  const currentDate = new Date(dateString);
  const minDate = new Date(minDateString);
  // Check if the given date is a valid date
  if (isNaN(currentDate.getTime())) {
    return false;
  }
  // Check if the given date is greater than the minimum date
  return currentDate > minDate;
};
/**
 * Performs a date check based on a condition (min, max, or exact match).
 *
 * @param currentValue - The current date value to validate.
 * @param filterValue - The filter value (e.g., minimum or maximum date).
 * @param condition - The condition to apply ("min" for greater than, "max" for less than, or exact match).
 * @returns `true` or `false` based on the condition check.
 */
const SdsDtDateCheck = (
  currentValue: any,
  filterValue: any,
  condition: string
) => {
  if (condition === "min") {
    return SdsDtIsValidAndGreaterThanMinDate(currentValue, filterValue);
  } else if (condition === "max") {
    return SdsDtIsValidAndGreaterThanMinDate(filterValue, currentValue);
  } else {
    return currentValue === filterValue;
  }
};
/**
 * Changes the format of a date string and converts it to the user's local time zone.
 *
 * @param dateString - The date string in UTC format.
 * @param format - The desired date format (default: "DD/MM/YYYY").
 * @returns The formatted date string in the local time zone.
 */
const SdsDtChangeDateFormatTimeZone = (
  dateString: string,
  format: string = "DD/MM/YYYY"
) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const selectedDateInUTC = moment_timzone.utc(dateString); // Assume the date is already in UTC
  const selectedDateInTimeZone = selectedDateInUTC.tz(userTimezone);
  const dateAlone = selectedDateInTimeZone.format(format);
  return dateAlone;
};
/**
 * Changes the format of a given date string.
 *
 * @param dateString - The date string to reformat.
 * @param format - The desired date format (e.g., "YYYY-MM-DD").
 * @returns The formatted date string, or the original string if invalid.
 */
const SdsDtChangeDateFormat = (dateString: string, format: string) => {
  try {
    const formattedDate = moment(dateString).format(format);
    return formattedDate;
  } catch (error) {
    // If an error occurs (e.g., invalid date), return the original string
    return dateString;
  }
};
/**
 * Retrieves the full month name based on the given month number.
 *
 * @param monthNumber - The month number (1 for January, 12 for December).
 * @returns The full name of the month, or `undefined` if the number is out of range.
 */
const SdsDtGetMonthNameWithNumber = (monthNumber: number) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return months[monthNumber - 1];
};
/**
 * Calculates the relative time difference from a given timestamp to now (e.g., "2 days ago").
 *
 * @param timestamp - The timestamp to calculate the difference from.
 * @returns A string representing the time difference (e.g., "5 minutes ago").
 */
const SdsDtGetTimeAgo = (timestamp: any) => {
  const currentTime: any = new Date();
  const previousTime: any = new Date(timestamp);
  const timeDifference: any = currentTime - previousTime;
  const seconds = Math.floor(timeDifference / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  } else if (hours > 0) {
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  } else if (minutes > 0) {
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  } else {
    return `${seconds} ${seconds === 1 ? "second" : "seconds"} ago`;
  }
};
/**
 * Converts a UTC time string (e.g., "15:30:00") into the local time.
 *
 * @param time_input - The UTC time string to convert.
 * @returns The local time string in "HH:mm" format.
 */
const SdsDtGetLocalTime = (time_input: string) => {
  if (time_input.length > 4) {
    const utcDate = new Date(`1970-01-01T${time_input}Z`); // Z indicates UTC
    // Convert to local time
    const localHours = utcDate.getHours();
    const localMinutes = utcDate.getMinutes();
    const localSeconds = utcDate.getSeconds();
    const localTime = `${localHours.toString().padStart(2, "0")}:${localMinutes
      .toString()
      .padStart(2, "0")}`;
    return localTime;
  }
};

/**
 * Checks if a given date falls on a weekend.
 *
 * @param dateString - The date string to check (in ISO format or valid date format).
 * @returns True if the date is a weekend, otherwise false.
 */
const SdsDtIsWeekend = (dateString: string): boolean => {
  const date = new Date(dateString);
  const day = date.getDay();
  return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
};

/**
 * Calculates the difference in days between two dates.
 *
 * @param startDate - The start date string.
 * @param endDate - The end date string.
 * @returns The number of days between the two dates.
 */
const SdsDtGetDaysDifference = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
};

/**
 * Adds a specified number of days to a date.
 *
 * @param dateString - The date string to add days to.
 * @param days - The number of days to add.
 * @returns A new date string after adding the days.
 */
const SdsDtAddDaysToDate = (dateString: string, days: number): string => {
  const date = new Date(dateString);
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0]; // Return in YYYY-MM-DD format
};

/**
 * Gets the start of the week (Monday) for a given date.
 *
 * @param dateString - The date string to calculate from.
 * @returns A date string representing the start of the week.
 */
const SdsDtGetStartOfWeek = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  const startOfWeek = new Date(date.setDate(diff));
  return startOfWeek.toISOString().split("T")[0]; // Return in YYYY-MM-DD format
};

/**
 * Checks if a string is a valid date.
 *
 * @param dateString - The date string to validate.
 * @returns True if the string is a valid date, otherwise false.
 */
const SdsDtIsValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Formats a date as a relative time (e.g., "3 days ago", "in 2 days").
 *
 * @param dateString - The date string to format.
 * @returns A relative time string.
 */
const SdsDtFormatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays > 0) return `In ${diffDays} day${diffDays > 1 ? "s" : ""}`;
  return `${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? "s" : ""} ago`;
};

/**
 * Gets the quarter of a given date.
 *
 * @param dateString - The date string to check.
 * @returns The quarter (1, 2, 3, or 4).
 */
const SdsDtGetQuarter = (dateString: string): number => {
  const date = new Date(dateString);
  const month = date.getMonth() + 1; // January is 0
  return Math.ceil(month / 3);
};

/**
 * Gets the last date of the month for a given date.
 *
 * @param dateString - The date string to calculate from.
 * @returns A date string representing the last day of the month.
 */
const SdsDtGetLastDateOfMonth = (dateString: string): string => {
  const date = new Date(dateString);
  const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return lastDate.toISOString().split("T")[0]; // Return in YYYY-MM-DD format
};

/**
 * Checks if a given year is a leap year.
 *
 * @param year - The year to check.
 * @returns True if the year is a leap year, otherwise false.
 */
const SdsDtIsLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

export {
    SdsDtAddDaysToDate, SdsDtChangeDateFormat, SdsDtChangeDateFormatTimeZone, SdsDtDateCheck, SdsDtFormatRelativeTime, SdsDtGetDaysDifference, SdsDtGetLastDateOfMonth, SdsDtGetLocalTime, SdsDtGetMonthNameWithNumber, SdsDtGetQuarter, SdsDtGetStartOfWeek, SdsDtGetTimeAgo, SdsDtIsLeapYear, SdsDtIsValidAndGreaterThanMinDate, SdsDtIsValidDate, SdsDtIsWeekend
};

